// src/components/PickField.jsx
// Campo di scelta con ricerca: mostra l'elemento scelto (foto, nome, dettaglio)
// oppure un campo di ricerca con l'elenco. Usato per scegliere auto e pilota
// nel setup dell'allenamento e per il cambio pilota durante la sessione.
//   items     [{ id, label, sub, img, disabled, note }]  (disabled: visibile ma non sceglibile)
//   value     id scelto (o null)
//   onChange  (id | null) => void
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './icons';
import './PickField.css';

const norm = (s = '') => String(s).normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

export default function PickField({
  items = [], value = null, onChange, placeholder = 'Cerca…', emptyText = 'Nessun risultato',
  icon: ItemIcon = null, clearLabel = 'Cambia', autoFocus = false, disabled = false,
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const selected = value != null && value !== '' ? items.find(i => String(i.id) === String(value)) || null : null;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const results = useMemo(() => {
    const nq = norm(q.trim());
    const hit = items.filter(i => !nq || norm(`${i.label} ${i.sub || ''}`).includes(nq));
    // prima quelli sceglibili, poi quelli già usati (con il motivo)
    return [...hit.filter(i => !i.disabled), ...hit.filter(i => i.disabled)].slice(0, 40);
  }, [items, q]);
  const pickable = results.filter(i => !i.disabled);

  const pick = (item) => {
    if (!item || item.disabled) return;
    onChange && onChange(String(item.id));
    setQ(''); setOpen(false); setHi(0);
  };

  if (selected) {
    return (
      <div className="pf-chip">
        {selected.img
          ? <img className="pf-chip__img" src={selected.img} alt="" />
          : <span className="pf-chip__img pf-chip__img--ph">{ItemIcon ? <ItemIcon size={16} /> : null}</span>}
        <span className="pf-chip__text">
          <strong title={selected.label}>{selected.label}</strong>
          {selected.sub ? <span title={selected.sub}>{selected.sub}</span> : null}
        </span>
        {!disabled && (
          <button type="button" className="pf-chip__clear" title={clearLabel} aria-label={clearLabel}
            onClick={() => { onChange && onChange(null); setQ(''); setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}>
            <Icon.X size={14} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="pf" ref={boxRef}>
      <div className="rs-search pf-input">
        <Icon.Search />
        <input
          ref={inputRef}
          type="text"
          className="rs-input"
          placeholder={placeholder}
          value={q}
          autoFocus={autoFocus}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setHi(0); }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi(h => Math.min(h + 1, Math.max(0, pickable.length - 1))); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(0, h - 1)); }
            else if (e.key === 'Enter') { e.preventDefault(); pick(pickable[hi] || pickable[0]); }
            else if (e.key === 'Escape') { setOpen(false); }
          }}
        />
      </div>
      {open && (
        <div className="pf-results" role="listbox">
          {results.length === 0 ? (
            <div className="pf-empty">{emptyText}</div>
          ) : results.map(item => {
            const idx = pickable.indexOf(item);
            return (
              <button
                key={item.id}
                type="button"
                role="option"
                aria-selected={idx === hi}
                aria-disabled={!!item.disabled}
                className={`pf-item${idx === hi ? ' is-hi' : ''}${item.disabled ? ' is-disabled' : ''}`}
                onMouseEnter={() => idx >= 0 && setHi(idx)}
                onClick={() => pick(item)}
                disabled={!!item.disabled}
              >
                {item.img
                  ? <img src={item.img} alt="" />
                  : <span className="pf-item__ph">{ItemIcon ? <ItemIcon size={15} /> : null}</span>}
                <span className="pf-item__text">
                  <strong>{item.label}</strong>
                  <span>{item.disabled && item.note ? item.note : item.sub || ''}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
